// Reinstalled 2026-08-02 as a clean, standalone GA4 tag — the previous
// G-4LTGC6EB6Q was merged with a second "Google tag" (GT-5D4TG2C4) at some
// point, and that merged tag silently stopped forwarding hits (gtag.js loaded
// and ran fine, but never actually made a network request — confirmed via
// Resource Timing/CSP-violation listeners, not just missing GA4 reports).
// This ID is unmerged; don't combine it with another tag in GA4 admin.
export const GTAG_ID = 'G-2LPMB3EWK3';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Google tag (gtag.js) bootstrap.
 * ---------------------------------------------------------------------------
 * Previously an inline <script> in index.html — CSP's script-src has no
 * 'unsafe-inline'/nonce/hash, so the browser silently blocked it and
 * window.gtag was never actually defined in production. Consent updates and
 * page-view tracking in utils/analytics.ts were no-ops despite gtag.js
 * itself loading fine (it's an external, CSP-allowed script). Defined here
 * instead, since this file ships as part of the CSP-allowed 'self' bundle.
 *
 * The dataLayer/consent-default stub is synchronous and free (no network),
 * so it runs immediately — Consent Mode v2 needs the default state queued
 * before the actual library arrives. The gtag.js library itself is then
 * injected once the page goes idle rather than as a static <script> tag, so
 * its ~800ms of main-thread execution doesn't compete with initial paint.
 */
export function initGtag(): void {
  window.dataLayer = window.dataLayer || [];
  // Matches Google's canonical snippet (`function(){dataLayer.push(arguments)}`)
  // verbatim rather than spreading into a new array — while never confirmed
  // as the actual cause of the previous tag's silent hit-drop, it removes any
  // doubt that gtag.js's own command parser sees exactly what it expects.
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('consent', 'default', { analytics_storage: 'denied' });
  window.gtag('js', new Date());
  window.gtag('config', GTAG_ID);

  const loadLibrary = () => {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
    document.head.appendChild(s);
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(loadLibrary, { timeout: 4000 });
  } else {
    setTimeout(loadLibrary, 1500);
  }
}
